import { Children, createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

const Leaving = createContext(false);

/** Whether the surface is on its way out: drawn a moment longer, fading, and no longer pressable. */
export function useLeaving(): boolean {
  return useContext(Leaving);
}

const motionAllowed = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

/**
 * Keeps what it last drew for [ms] after its children go, so a panel can ease out rather than
 * vanish. The same tree is drawn throughout, so a panel reopened while leaving is the one already
 * there and not drawn anew. Where motion is not wanted, or unknown, it goes at once.
 */
export function Presence({ children, ms = 150 }: { children: ReactNode; ms?: number }) {
  const shown = Children.toArray(children).length > 0;
  const [last, setLast] = useState<ReactNode>(null);
  const [held, setHeld] = useState<ReactNode>(null);
  if (shown && last !== children) setLast(children);
  if (shown && held !== null) setHeld(null);
  if (!shown && last !== null) {
    setLast(null);
    if (motionAllowed()) setHeld(last);
  }
  useEffect(() => {
    if (held === null) return;
    const id = setTimeout(() => setHeld(null), ms);
    return () => clearTimeout(id);
  }, [held, ms]);
  const leaving = !shown && held !== null;
  return <Leaving.Provider value={leaving}>{shown ? children : held}</Leaving.Provider>;
}
